import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
} from "react";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import useBoundingclientrectRef from "@rooks/use-boundingclientrect-ref";
import { useSpring, animated } from "react-spring";

import classnames from "classnames";

import { useDrag } from "react-use-gesture";

import Node from "../components/Node";
import Connector from "../components/GradientConnector";
import UConnector from "../components/UConnector";
import LConnector from "../components/LConnector";
import SConnector from "../components/SConnector";
import Overlay from "../components/Overlay";
import useWindowSize from "./useWindowSizeHook";

import {
  SCENARIOS,
  SCENARIOS_META,
  YEARS,
  processData,
  getCustomVariables,
  co2connectorsDict,
} from "./data";
import rawData from "./rawData";
import FullscreenButton from "#/svg/fullscreen.svg";
import ExitFullscreenButton from "#/svg/exit-fullscreen.svg";

export const HAIRLINE_WIDTH = 1.2;
export const DOTS_WIDTH = 2;
export const DOTS_SPREAD_RATIO = 2;
const ANIM_SPEED = 0.05;
export const BASE_H = 650;
export const BASE_W = 1200;

const mq = window.matchMedia("(orientation: portrait)");
const SCENARIOS_DROPDOWN_ORDER = [1, 0, 2, 3, 4];

export default (dataProp) => () => {
  const [data, setData] = useState(dataProp);
  const [dataDump, setDataDump] = useState("");
  const [playing, setPlaying] = useState(false);

  const [yearId, setYearId] = useState(0);
  const [yearMenu, setYearMenu] = useState(false);
  const [scenarioId, setScenarioId] = useState(1);
  const [scenarioMenu, setScenarioMenu] = useState(false);

  const [useDND, setUseDND] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [overlayVisible, setOverlayVisible] = useState(
    localStorage.getItem("visualizer_guide") !== null ? false : true
  );
  const [fullscreen, setFullscreen] = useState(false);
  const windowSize = useWindowSize();
  const [expandedNodeIds, setExpandedNodeIds] = useState([]);
  const [random, setRandom] = useState(Math.random());
  const [scale, setScale] = useState(
    parseFloat(localStorage.getItem("visualizer_scale_2")) || 1
  );
  const [isManipulating, setIsManipulating] = useState(false);

  const [{ xy }, set] = useSpring(() => ({ xy: [0, 0] }));
  const [initXY, setInitXY] = useState([0, 0]);
  const [contRect, setContRect] = useState({ w: 0, h: 0 });

  const diagRef = useRef(null);
  const controlsRef = useRef(null);
  const wrapperRef = useRef(null);
  const contRef = useRef(null);
  const [tooltip, setTooltip] = useState({
    x: 0,
    y: 0,
    text: "",
    color: "transparent",
    visible: false,
  });

  const scrollIntoView = () => {
    if (windowSize.height < 700 && windowSize.width > 450) {
      diagRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(() => {
    const header = document.querySelector(".header");
    header.classList.add("is-absolute");
    setTimeout(() => setRandom(Math.random()));
    mq.addListener(() => {
      setTimeout(() => setRandom(Math.random()));
    });
  }, []);

  useEffect(() => {
    const cR = contRef.current.getBoundingClientRect();
    let cRect = controlsRef.current.getBoundingClientRect();
    setContRect(cR);
    let autoscale = 1;
    let marginTop = 0;
    if (windowSize.height) {
      // console.log(windowSize.height, controlsRef.current.getBoundingClientRect().height, cR.height);

      if (windowSize.width > 450) {
        autoscale = Math.min(
          fullscreen ? 1.4 : 1,
          Math.max(
            0.6,
            Math.min(
              windowSize.height / (BASE_H + cRect.height),
              windowSize.width / (BASE_W + 50)
            )
          )
        );
        marginTop = 0;
        if (windowSize.height < 700) {
          marginTop = 10;
        }
      } else {
        autoscale = (windowSize.height - 180) / BASE_H;
        marginTop = -cR.height / 20;
      }
      // console.log({
      //   autoscale, marginTop, cR: cR.height
      // })
      setInitXY([
        cRect.width / 2 - BASE_W / 2 - 20,
        cR.height / 2 - BASE_H / 2 + (fullscreen ? -120 : 0) + marginTop,
      ]);

      setScale(autoscale);
    }
  }, [windowSize.height, windowSize.width, controlsRef, fullscreen, random]);

  // useEffect(() => {
  //   if (!fullscreen) {
  //     setTimeout(() => {
  //       set({xy: [0, 0]})
  //       scrollIntoView();
  //     },200
  //   );
  //   }
  // }, [fullscreen, set])

  useEffect(() => {
    const keyUpListener = (e) => {
      switch (e.key) {
        case "ArrowDown":
          e.stopPropagation();
          setScenarioId(scenarioId < SCENARIOS.length - 1 ? scenarioId + 1 : 0);
          break;
        case "ArrowUp":
          e.stopPropagation();
          setScenarioId(scenarioId > 0 ? scenarioId - 1 : SCENARIOS.length - 1);
          break;
        case "ArrowRight":
          e.stopPropagation();
          setYearId(yearId < YEARS.length - 1 ? yearId + 1 : 0);
          break;
        case "ArrowLeft":
          e.stopPropagation();
          setYearId(yearId > 0 ? yearId - 1 : YEARS.length - 1);
          break;
      }
    };

    document.body.addEventListener("keyup", keyUpListener);

    return () => {
      document.body.removeEventListener("keyup", keyUpListener);
    };
  }, [scenarioId, yearId]);

  useEffect(() => {
    let raf;

    const update = () => {
      let cnt = 0;
      data.connectors.forEach((connector, index) => {
        if (connector.target) {
          for (let prop in connector.target) {
            cnt++;

            const delta = connector.target[prop] - connector[prop];

            if (Math.abs(delta) < 0.3) {
              connector[prop] = connector.target[prop];
              delete connector.target[prop];
              // delete connector.target[`${prop}_initial`];
            }
            connector[prop] += ANIM_SPEED * delta;
          }
        }
      });
      data.CO2connectors.forEach((connector, index) => {
        if (connector.target) {
          for (let prop in connector.target) {
            cnt++;
            // if (!(`${prop}_initial` in connector.target)) {
            //   connector.target[`${prop}_initial`] = connector[prop];
            // }

            const delta = connector.target[prop] - connector[prop];

            if (Math.abs(delta) < 0.3) {
              connector[prop] = connector.target[prop];
              delete connector.target[prop];
              // delete connector.target[`${prop}_initial`];
            }
            connector[prop] += ANIM_SPEED * delta;
          }
        }
      });

      if (cnt > 0) {
        setData({ ...data });
        raf = requestAnimationFrame(update);
      } else {
        if (playing) {
          setTimeout(() => {
            setYearId((yearId + 1) % YEARS.length);
          });
        }
      }
    };
    raf = requestAnimationFrame(update);

    return () => {
      cancelAnimationFrame(raf);
    };
  }, [yearId, scenarioId, playing]);

  useEffect(() => {
    data.connectors.forEach((connector, index) => {
      connector.target = {};
      for (let prop in connector.series) {
        connector.target[prop] = connector.series[prop][scenarioId][yearId];
      }
      connector.target.w = connector.normalized.w[scenarioId][yearId];
      // connector.target.rawW = connector.normalized.rawW[scenarioId][yearId];
      connector.target.fPos = connector.normalized.fPos[scenarioId][yearId];
      connector.target.tPos = connector.normalized.tPos[scenarioId][yearId];
    });
    data.CO2connectors.forEach((connector, index) => {
      connector.target = {};
      for (let prop in connector.series) {
        connector.target[prop] = connector.series[prop][scenarioId][yearId];
      }
      connector.target.w = connector.normalized.w[scenarioId][yearId];
      connector.target.rawW = connector.normalized.rawW[scenarioId][yearId];
      connector.target.fPos = connector.normalized.fPos[scenarioId][yearId];
      connector.target.tPos = connector.normalized.tPos[scenarioId][yearId];
    });

    setData({ ...data });

    if (yearId !== YEARS.length - 1) {
      setExpandedNodeIds([]);
    }
  }, [yearId, scenarioId]);

  const bind = useDrag(({ args: [index], event, delta: [dx, dy] }) => {
    data.nodes[index].x += dx;
    data.nodes[index].y += dy;

    setData({ ...data });
  });

  const bindSpringDrag = useDrag(
    ({ event, initial, down, offset: [oX, oY], cancel }) => {
      if (isManipulating !== down) {
        setIsManipulating(down);
      }

      // console.log(JSON.stringify({initial}));
      if (expandedNodeIds.length === 0 && windowSize.width < 750) {
        event.preventDefault();
        event.stopPropagation();
        set({ xy: [oX, oY] });
      }
    },
    {
      delay: true,
      eventOptions: { passive: false },
      domTarget: contRef,
      initial: [0, 0],
      filterTaps: true,
      bounds: {
        top: -50,
        right: BASE_W * scale * 0.33,
        bottom: 50,
        left: -BASE_W * scale * 0.33,
      },
    }
  );

  // console.log({xy})
  const fsHandle = useFullScreenHandle();

  const handleFSchange = useCallback(
    (state, handle) => {
      setFullscreen(state);
    },
    [fsHandle]
  );

  const { nodes, connectors, CO2connectors, calculated } = data;

  return (
    <FullScreen handle={fsHandle} onChange={handleFSchange}>
      <div ref={wrapperRef} className="visualiser__wrapper">
        <div
          ref={diagRef}
          className="visualiser"
          id="visualiser"
          onClick={scrollIntoView}
          onTouchEnd={scrollIntoView}
        >
          <div ref={controlsRef} className="visualiser__controls">
            <div className="visualiser__controls--left">
              <p className="visualiser__scenario-number">
                {window.backendVars.copy["Scenario"]}{" "}
                {SCENARIOS_DROPDOWN_ORDER.indexOf(scenarioId) + 1} /{" "}
                {SCENARIOS.length}
              </p>
              <div
                className={classnames("visualiser__select", {
                  active: scenarioMenu,
                })}
                onClick={() => {
                  setScenarioMenu(!scenarioMenu);
                  setYearMenu(false);
                }}
              >
                <span className="visualiser__select--label">
                  {SCENARIOS_META[scenarioId].title}
                </span>
                <div
                  className={classnames("visualiser__select--icon", {
                    active: scenarioMenu,
                  })}
                />
                <div
                  className={classnames("visualiser__menu", {
                    visible: scenarioMenu,
                  })}
                >
                  {SCENARIOS_DROPDOWN_ORDER.map((idx) => {
                    return (
                      <div
                        className={classnames("visualiser__menu__item", {
                          active: idx === scenarioId,
                        })}
                        onClick={() => {
                          setScenarioMenu(false);
                          setYearMenu(false);
                          setScenarioId(idx);
                        }}
                        key={`menu-${idx}`}
                      >
                        <h5>{SCENARIOS_META[idx].title}</h5>
                        <p>{SCENARIOS_META[idx].description}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div
                className="simulator-overlay-toggle"
                onClick={() => setOverlayVisible(true)}
                style={{
                  visibility: !overlayVisible ? "visible" : "hidden",
                  opacity: !overlayVisible ? 1 : 0,
                }}
              >
                <i>i</i>
                <span>{window.backendVars.copy["See info"]}</span>
              </div>
            </div>

            <div className="visualiser__controls--right">
              <div className="visualiser__controls__dates">
                {YEARS.map((sc, idx) => (
                  <div
                    className={classnames("visualiser__controls__year", {
                      active: idx === yearId,
                    })}
                    onClick={(e) => {
                      setYearId(idx);
                    }}
                    key={`year-${idx}`}
                  >
                    {sc}
                  </div>
                ))}
                <div
                  className={classnames("visualiser__controls__year-select", {
                    active: yearMenu,
                  })}
                  onClick={(e) => {
                    setYearMenu(!yearMenu);
                    setScenarioMenu(false);
                  }}
                >
                  {YEARS[yearId]}
                  <div
                    className={classnames("visualiser__select--icon", {
                      active: yearMenu,
                    })}
                  />
                </div>
                <div
                  className={classnames("visualiser__menu year", {
                    visible: yearMenu,
                  })}
                >
                  {YEARS.map((year, idx) => (
                    <div
                      className={classnames("visualiser__menu__item", {
                        active: idx === yearId,
                      })}
                      onClick={() => {
                        setYearMenu(false);
                        setYearId(idx);
                      }}
                      key={`menu-year-${idx}`}
                    >
                      <h5>{year}</h5>
                    </div>
                  ))}
                </div>
              </div>

              <div
                className="visualiser__play"
                onClick={() => {
                  setPlaying(!playing);
                }}
              >
                <div
                  className={classnames("visualiser__playbutton", {
                    pause: playing,
                  })}
                />
                <span className="visualiser__playpause">
                  {playing
                    ? window.backendVars.copy["Pause"]
                    : window.backendVars.copy["Play"]}
                </span>
              </div>
            </div>
          </div>
          <div
            className="visualiser__container"
            {...bindSpringDrag()}
            style={{
              maxHeight: fullscreen ? "none" : (BASE_H * scale || 1) + 50,
            }}
            dir="ltr"
            ref={contRef}
            onMouseMove={(e) => {
              if (isManipulating) return;
              if (tooltip.visible) {
                const tooltipNode = document.getElementById(
                  "visualiser-tooltip"
                );
                tooltipNode.style.top = `${
                  e.nativeEvent.clientY +
                  window.pageYOffset -
                  e.currentTarget.offsetTop -
                  e.currentTarget.offsetParent.offsetTop
                }px`;
                tooltipNode.style.left = `${
                  e.nativeEvent.clientX +
                  window.pageXOffset -
                  e.currentTarget.offsetLeft -
                  e.currentTarget.offsetParent.offsetLeft
                }px`;
              }
            }}
          >
            {!overlayVisible && (
              <div
                onClick={
                  fullscreen
                    ? () => {
                        fsHandle.exit();
                        setTimeout(() => setRandom(Math.random()), 1500);
                      }
                    : fsHandle.enter
                }
                className="visualiser__fullscreen-btn"
              >
                {fullscreen ? <ExitFullscreenButton /> : <FullscreenButton />}
              </div>
            )}
            <animated.div
              className="visualiser__dragger"
              style={{
                width: `${BASE_W}px`,
                height: `${BASE_H}px`,
                pointerEvents: isManipulating ? "none" : "auto",
                transform: xy.interpolate(
                  (x, y) =>
                    `translate3d(${initXY[0] + x}px, ${
                      initXY[1] + y + (fullscreen ? 100 : 0)
                    }px, 0) scale(${scale})`
                ),
              }}
              dir={
                document.documentElement.getAttribute("dir") === "rtl"
                  ? "rtl"
                  : "ltr"
              }
            >
              {nodes.map((node, index) => {
                let editableMax = 10;
                if (typeof node.editableMax === "string") {
                  editableMax =
                    calculated[scenarioId][node.editableMax][yearId];
                }
                let editableMin = 0;
                if (typeof node.editableMin === "string") {
                  editableMin =
                    calculated[scenarioId][node.editableMin][yearId];
                }

                const editingDisabled = false;
                // disable NES, DAC if NTA is smaller than 0;
                // const editingDisabled =
                //   CO2connectors[co2connectorsDict['atm-nta']].series.data[
                //     scenarioId
                //   ][yearId] < 0 && ['nes', 'dac'].indexOf(node.id) > -1;

                return (
                  <Node
                    key={`node-${index}`}
                    {...(useDND ? { ...bind(index) } : {})}
                    x={node.x}
                    y={node.y}
                    width={node.w}
                    height={node.h}
                    color={node.color}
                    borderColor={node.borderColor}
                    node={node}
                    className={node.className}
                    icon={node.icon}
                    label={node.label}
                    yearId={yearId}
                    scenarioId={scenarioId}
                    editMode={editMode}
                    editableMax={editableMax}
                    editableMin={editableMin}
                    editingDisabled={editingDisabled}
                    expandedNodeIds={expandedNodeIds}
                    onToggle={(id) => {
                      let index = expandedNodeIds.indexOf(id);

                      if (index === -1) {
                        setExpandedNodeIds([...expandedNodeIds, id]);
                      } else {
                        setExpandedNodeIds(
                          expandedNodeIds.filter((nid) => id !== nid)
                        );
                      }
                    }}
                    onChangeData={(rel, value) => {
                      const cv = getCustomVariables(rawData);
                      cv[scenarioId][rel][yearId] = parseFloat(value);
                      setData(processData(rawData, cv, scenarioId, yearId));
                    }}
                    onChange={(
                      coll,
                      prop,
                      v,
                      connIdx,
                      sIdx,
                      yIdx,
                      forceAllYears = false
                    ) => {
                      // console.log({ v, connIdx, sIdx, yIdx });
                      node[coll][connIdx][prop] = parseFloat(v);
                      node[coll][connIdx].series[prop][sIdx][yIdx] = parseFloat(
                        v
                      );
                      if (forceAllYears) {
                        YEARS.forEach((y, idx) => {
                          node[coll][connIdx].series[prop][sIdx][
                            idx
                          ] = parseFloat(v);
                        });
                      }
                      setData({ ...data });
                    }}
                  />
                );
              })}
              <svg
                width={BASE_W}
                height={BASE_H}
                style={{
                  pointerEvents: isManipulating ? "none" : "auto",
                  userSelect: "none",
                }}
              >
                <defs>
                  <linearGradient
                    id={`grad_co2`}
                    x1="0%"
                    y1="100%"
                    x2="0%"
                    y2="0%"
                    key={`gradient-co2`}
                  >
                    <stop
                      offset="0%"
                      style={{
                        stopColor: "#4AC9D3",
                        stopOpacity: 0.2,
                      }}
                    />
                    <stop
                      offset="100%"
                      style={{
                        stopColor: "#4AC9D3",
                        stopOpacity: 0.9,
                      }}
                    />
                  </linearGradient>
                  <linearGradient
                    id={`grad_co2_hover`}
                    x1="0%"
                    y1="100%"
                    x2="0%"
                    y2="0%"
                    key={`gradient-co2-hover`}
                  >
                    <stop
                      offset="0%"
                      style={{
                        stopColor: "#4AC9D3",
                        stopOpacity: 0.9,
                      }}
                    />
                    <stop
                      offset="100%"
                      style={{
                        stopColor: "#4AC9D3",
                        stopOpacity: 0.9,
                      }}
                    />
                  </linearGradient>

                  {nodes.map((node, index) => (
                    <React.Fragment key={index}>
                      <linearGradient
                        id={`grad_${index}`}
                        x1="0%"
                        y1="100%"
                        x2="0%"
                        y2="0%"
                        key={`gradient-${index}`}
                      >
                        <stop
                          offset="0%"
                          style={{
                            stopColor: node.outColor || node.color,
                            stopOpacity: 0.2,
                          }}
                        />
                        <stop
                          offset="100%"
                          style={{
                            stopColor: node.outColor || node.color,
                            stopOpacity: 0.9,
                          }}
                        />
                      </linearGradient>
                      <linearGradient
                        id={`grad_${index}_hover`}
                        x1="0%"
                        y1="100%"
                        x2="0%"
                        y2="0%"
                        key={`gradient-${index}-hover`}
                      >
                        <stop
                          offset="0%"
                          style={{
                            stopColor: node.outColor || node.color,
                            stopOpacity: 0.9,
                          }}
                        />
                        <stop
                          offset="100%"
                          style={{
                            stopColor: node.outColor || node.color,
                            stopOpacity: 1,
                          }}
                        />
                      </linearGradient>
                    </React.Fragment>
                  ))}
                </defs>
                {connectors.map((connector, index) => {
                  const from = { ...connector.from };
                  const to = { ...connector.to };

                  const f =
                    data.nodes[from.nodeIndex] && data.nodes[from.nodeIndex];
                  const t =
                    data.nodes[to.nodeIndex] && data.nodes[to.nodeIndex];

                  // console.log({ toNode, fromNode });
                  const tx = t.x + connector.tPos; // t.w * (connector.tPos / 100);
                  const ty = t.y + t.h;

                  const fx = f.x + connector.fPos; // f.w * (connector.fPos / 100);
                  const fy = f.y;

                  return (
                    f &&
                    t && (
                      <Connector
                        key={`connector-${index}`}
                        from={{ x: fx, y: fy }}
                        to={{ x: tx, y: ty }}
                        width={connector.w}
                        turn={connector.turn}
                        radius={connector.r}
                        fromIndex={from.nodeIndex}
                        color={connector.color}
                        conn={connector}
                        yearId={yearId}
                        scenarioId={scenarioId}
                        showTooltip={(data) => {
                          if (data.visible === false) {
                            setTooltip({ ...tooltip, visible: false });
                          } else {
                            setTooltip({
                              ...data,
                              unit: " EJ",
                              connIdx: index,
                              text: connector.series.data[scenarioId][yearId],
                              color: f.outColor || f.borderColor,
                            });
                          }
                        }}
                      />
                    )
                  );
                })}
                {CO2connectors.map((connector, index) => {
                  const from = { ...connector.from };
                  const to = { ...connector.to };

                  const f =
                    data.nodes[from.nodeIndex] && data.nodes[from.nodeIndex];
                  const t =
                    data.nodes[to.nodeIndex] && data.nodes[to.nodeIndex];

                  // console.log({ toNode, fromNode });
                  if (!f || !t) {
                    return;
                  }

                  let tx = t.x + connector.tPos; // t.w * (connector.tPos / 100);
                  let ty = t.y + t.h;

                  let fx = f.x + connector.fPos; // f.w * (connector.fPos / 100);
                  let fy = f.y;

                  if (!("type" in connector)) {
                    if (f.y < t.y) {
                      ty = t.y;
                      fy = f.y + f.h;
                    }
                  }
                  if (connector.type === "s") {
                    tx = t.x + (connector.side.to === "r" ? t.w : 0);
                    ty = t.y + t.h / 2;
                    fx = f.x;
                    fy = f.y + f.h / 2;
                  }

                  if (connector.type === "u") {
                    ty = t.y + (connector.side.to === "b" ? t.h : 0);
                    fy = f.y + (connector.side.from === "b" ? f.h : 0);
                  }

                  if (connector.type === "l") {
                    ty = t.y + (connector.side.to === "b" ? t.h : 0);
                    fx =
                      f.x + (connector.side.from.indexOf("r") > -1 ? f.w : 0);
                    if (connector.side.from.indexOf("c") > -1) {
                      fy = f.y + f.h / 2;
                    } else {
                      fy = f.y + connector.fPos;
                    }
                  }

                  let ConnectorComponent = Connector;

                  if (connector.type) {
                    switch (connector.type) {
                      case "u":
                        ConnectorComponent = UConnector;
                        break;
                      case "l":
                        ConnectorComponent = LConnector;
                        break;
                      case "s":
                        ConnectorComponent = SConnector;
                        break;
                      default:
                    }
                  }

                  return (
                    f &&
                    t && (
                      <ConnectorComponent
                        key={`co2connector-${index}`}
                        from={{ x: fx, y: fy }}
                        to={{ x: tx, y: ty }}
                        width={connector.w}
                        turn={connector.turn}
                        radius={connector.r}
                        fromIndex={from.nodeIndex}
                        color={connector.color}
                        stroke={
                          connector.type === "s" ? `#4AC9D3` : `url(#grad_co2)`
                        }
                        strokeHover={
                          connector.type === "s"
                            ? `#4AC9D3`
                            : `url(#grad_co2_hover)`
                        }
                        conn={connector}
                        yearId={yearId}
                        scenarioId={scenarioId}
                        showTooltip={(data) => {
                          if (data.visible === false) {
                            setTooltip({ ...tooltip, visible: false });
                          } else {
                            setTooltip({
                              ...data,
                              connIdx: index,
                              unit: " Gt CO₂",
                              text: connector.series.data[scenarioId][yearId],
                              color: "#4AC9D3",
                            });
                          }
                        }}
                      />
                    )
                  );
                })}
              </svg>
            </animated.div>
            <Overlay
              visible={overlayVisible}
              onChange={(visibility) => {
                console.log("CLICK");
                setOverlayVisible(visibility);
                localStorage.setItem("visualizer_guide", false);
              }}
            />
            <div
              className={"vis-tooltip"}
              id="visualiser-tooltip"
              style={{
                opacity: tooltip.visible ? 1 : 0,
                borderBottomColor: tooltip.color,
                color: tooltip.color,
                transformOrigin: "center",
                transform: `scale(${scale})`,
              }}
            >
              {parseFloat(tooltip.text).toFixed(3)} {tooltip.unit}
            </div>
          </div>
          <div className="debug__bar" style={{ display: "none" }}>
            Scale: &nbsp;
            <input
              className="debug__button"
              type="number"
              value={scale}
              step="0.02"
              min={"0.5"}
              max={"1.2"}
              onChange={(e) => {
                const sc = parseFloat(e.target.value);
                localStorage.setItem("visualizer_scale_2", 1);
                setScale(sc);
              }}
            />
            <button
              onClick={() => setEditMode(!editMode)}
              className="debug__button"
            >
              {editMode ? "Disable EDIT MODE" : "Enable EDIT MODE"}
            </button>
            {editMode && (
              <>
                <button
                  onClick={() => setUseDND(!useDND)}
                  className="debug__button"
                >
                  {useDND ? "Disable drag&drop" : "Enable drag&drop"}
                </button>
                <button
                  className="debug__button"
                  onClick={() => {
                    /* HACKY UGLY EXPORTER START */
                    const preEl = document.getElementById("jsonSource");
                    const dataToDump = JSON.parse(JSON.stringify(data));
                    dataToDump.nodes = dataToDump.nodes.map((node) => {
                      delete node.incoming;
                      delete node.incomingCO2;
                      delete node.incomingTotal2;
                      delete node.incomingTotalCO2;
                      delete node.inVPP2;
                      delete node.inVPPCO2;

                      delete node.outgoing;
                      delete node.outgoingCO2;
                      delete node.outgoingTotal2;
                      delete node.outgoingTotalCO2;
                      delete node.outVPP2;
                      delete node.outVPPCO2;
                      return node;
                    });
                    dataToDump.connectors = dataToDump.connectors.map(
                      (conn) => {
                        Object.keys(conn).forEach((key) => {
                          if (
                            ![
                              "rel",
                              "series",
                              "type",
                              "side",
                              "ignoreWidth",
                              "showText",
                              "dottedOnlyIf0",
                            ].includes(key)
                          ) {
                            delete conn[key];
                          }
                        });
                        return conn;
                      }
                    );
                    dataToDump.CO2connectors = dataToDump.CO2connectors.map(
                      (conn) => {
                        // zero out data
                        conn.series.data = SCENARIOS.map(() =>
                          YEARS.map(() => 0)
                        );
                        Object.keys(conn).forEach((key) => {
                          if (
                            ![
                              "rel",
                              "series",
                              "type",
                              "side",
                              "ignoreWidth",
                              "showText",
                              "dottedOnlyIf0",
                            ].includes(key)
                          ) {
                            delete conn[key];
                          }
                        });
                        return conn;
                      }
                    );
                    delete dataToDump.calculated;
                    setDataDump(
                      "export default " +
                        JSON.stringify(dataToDump, null, 2).replace(
                          "CO2connectors",
                          "co2connectors"
                        )
                    );
                    setTimeout(() => {
                      preEl.select();
                      document.execCommand("copy");
                      alert("Copied to clipboard!");
                    }, 300);
                  }}
                  /* HACKY UGLY EXPORTER END */
                >
                  Copy to clipboard
                </button>
                <br />
                <textarea
                  id="jsonSource"
                  style={{ display: "none" }}
                  value={dataDump}
                  onChange={() => {}}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </FullScreen>
  );
};
